import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Card,
  EmptyState,
  ErrorAlert,
  PageHeader,
  SegmentedControl,
  Skeleton,
  formatDay,
  formatPercent,
  formatUsd,
  formatUsdCompact,
  useDocumentTitle,
} from "@unizpay/ui";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { AreaChart } from "../components/AreaChart";
import { CreatePaymentLinkButton } from "../components/CreatePaymentLinkButton";
import { MetricCard } from "../components/MetricCard";
import { PaymentsTable } from "../components/PaymentsTable";
import { useProfileApi } from "../lib/profile";
import { useSettlementSettings } from "../lib/queries";
import type { Overview, OverviewDays } from "../lib/types";
import { VolumeByNetwork } from "./home/VolumeByNetwork";

const RANGES: Array<{ value: OverviewDays; label: string }> = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

function parseDays(value: string | null): OverviewDays {
  return value === "7" ? 7 : value === "90" ? 90 : 30;
}

function plural(n: number, word: string) {
  return `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
}

function AttentionCallout({ count }: { count: number }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warn/30 bg-warn-subtle px-4 py-3 text-warn">
      <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
      <p className="min-w-0 flex-1 text-[0.8125rem]">
        <span className="font-semibold">{plural(count, "payment")} need attention.</span> Underpaid, late or
        frozen payments may need you to act.
      </p>
      <Link
        to="/payments?status=attention"
        className="inline-flex items-center gap-1 rounded text-[0.8125rem] font-semibold underline-offset-2 hover:underline"
      >
        Review <ArrowRight aria-hidden="true" className="size-3.5" />
      </Link>
    </div>
  );
}

function Metrics({ data, loading }: { data: Overview | undefined; loading: boolean }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        label="Gross volume"
        loading={loading}
        value={data && formatUsd(data.gross_volume)}
        detail="Settled payments"
      />
      <MetricCard
        label="Successful payments"
        loading={loading}
        value={data?.payments.succeeded.toLocaleString()}
        detail={data && `of ${plural(data.payments.total, "payment")} created`}
      />
      <MetricCard
        label="Success rate"
        loading={loading}
        value={data && (data.success_rate === null ? "—" : formatPercent(data.success_rate))}
        detail="Excludes payments still open"
      />
      <MetricCard
        label="Platform fees"
        loading={loading}
        value={data && formatUsd(data.platform_fees)}
        detail="Flat $0.10 per successful payment"
      />
    </div>
  );
}

function VolumeChart({ data }: { data: Overview }) {
  const points = data.series.map((d) => ({ label: formatDay(d.date), value: Number(d.volume) }));
  const empty = points.every((p) => p.value === 0);
  return (
    <div className="relative">
      <AreaChart
        points={points}
        label={`Gross volume per day, last ${data.range.days} days`}
        formatTick={formatUsdCompact}
        tooltip={(i) => {
          const d = data.series[i];
          if (!d) return null;
          return (
            <>
              <span className="block font-medium">{formatDay(d.date, "long")}</span>
              <span className="block tabular-nums">
                {formatUsd(d.volume)} <span className="text-fg-muted">· {plural(d.count, "payment")}</span>
              </span>
            </>
          );
        }}
      />
      {empty && (
        <p className="pointer-events-none absolute inset-x-0 top-1/3 text-center text-[0.8125rem] text-fg-muted">
          No settled volume in this period
        </p>
      )}
    </div>
  );
}

export function HomePage() {
  useDocumentTitle("Home");
  const { api, mode, key } = useProfileApi();
  const settings = useSettlementSettings();
  const [params, setParams] = useSearchParams();
  const days = parseDays(params.get("days"));

  const overview = useQuery({
    queryKey: key("overview", days),
    queryFn: () => api.overview(mode, days),
    placeholderData: keepPreviousData,
  });
  const recent = useQuery({
    queryKey: key("payments", { limit: 5 }),
    queryFn: () => api.payments(mode, { limit: 5 }),
  });

  const data = overview.data;
  const loading = overview.isPending;

  return (
    <>
      <PageHeader
        title="Home"
        actions={
          <>
            <SegmentedControl
              label="Date range"
              options={RANGES}
              value={days}
              onChange={(d) => setParams(d === 30 ? {} : { days: String(d) }, { replace: true })}
            />
            <CreatePaymentLinkButton settlementReady={Boolean(settings.data?.active)} />
          </>
        }
      />
      <div className="space-y-6">
        {data && data.payments.attention > 0 && <AttentionCallout count={data.payments.attention} />}
        <ErrorAlert error={overview.error} title="Could not load your overview" />

        <Metrics data={data} loading={loading} />

        <Card
          title="Gross volume"
          description={`Settled payments per day, last ${days} days (UTC)`}
          actions={
            data && <span className="text-lg font-semibold tabular-nums">{formatUsd(data.gross_volume)}</span>
          }
        >
          {data ? <VolumeChart data={data} /> : <Skeleton className="h-[220px] w-full" />}
        </Card>

        <div className="grid gap-6 lg:grid-cols-5">
          <Card title="Volume by network" className="lg:col-span-2">
            <VolumeByNetwork rows={data?.by_network} />
          </Card>
          <Card
            title="Recent payments"
            className="lg:col-span-3"
            flush
            actions={
              <Link
                to="/payments"
                className="rounded text-[0.8125rem] font-medium text-accent hover:underline"
              >
                View all
              </Link>
            }
          >
            {recent.isError ? (
              <div className="p-4">
                <ErrorAlert error={recent.error} title="Could not load payments" />
              </div>
            ) : (
              <PaymentsTable
                compact
                rows={recent.data?.data}
                loading={recent.isPending}
                skeletonRows={5}
                empty={
                  <EmptyState title="No payments yet">
                    Create one with <code className="font-mono">POST /v1/payments</code> using a {mode} API
                    key.
                  </EmptyState>
                }
              />
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
